import { useEffect, useState } from "react";
import {
   ComposedChart,
   Bar,
   Line,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   Legend,
   ResponsiveContainer,
} from "recharts";
import { GET_MONTHLY_VEHICLE_DEFECTS_REPORT } from "../../../graphql/queries";
import { useLazyQuery } from "@apollo/client";
import RecordsModal, { RecordsFilter } from "../RecordsModal";

const formatMonth = (yyyymm: string) => {
   const year = yyyymm.slice(0, 4);
   const month = Number(yyyymm.slice(4, 6)) - 1;
   return new Date(Number(year), month).toLocaleString("en-US", {
      month: "short",
      year: "numeric",
   });
};

type MonthRow = { month: string; rawMonth: string; total: number; found: number; rectified: number; percent: number };

const VIEW_LABEL: Record<NonNullable<RecordsFilter["defectsView"]>, string> = {
   all: "Total Inspections",
   with_defects: "Found with Defect",
   rectified: "Rectified",
};

export default function MonthlyTruckDefectsChart() {
   const [data, setData] = useState<MonthRow[]>([]);
   const [selected, setSelected] = useState<{ title: string; filter: RecordsFilter } | null>(null);
   const [getMonthlyVehicleDefects, { loading }] = useLazyQuery(GET_MONTHLY_VEHICLE_DEFECTS_REPORT, {
      fetchPolicy: "no-cache",
   });

   useEffect(() => {
      getMonthlyVehicleDefects().then((res) => {
         setData(() =>
            (res.data?.getMonthlyVehicleDefects || []).map((r: any) => ({
               ...r,
               rawMonth: r.month,
               month: formatMonth(r.month),
            })),
         );
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   function openFor(view: NonNullable<RecordsFilter["defectsView"]>, payload: MonthRow | undefined) {
      if (!payload?.rawMonth) return;
      setSelected({
         title: `${VIEW_LABEL[view]} — ${payload.month}`,
         filter: { month: payload.rawMonth, defectsView: view },
      });
   }

   return (
      <div style={{ width: "100%", height: 420 }} className="border bottom-1 rounded p-2">
         <h3 style={{ textAlign: "center" }}>
            Monthly Truck Defects & Rectifications ({data.length} month(s))
         </h3>
         {loading && <p>Loading ....</p>}
         <ResponsiveContainer>
            <ComposedChart data={data} barGap={6} barCategoryGap={20}>
               <CartesianGrid strokeDasharray="3 3" />
               <XAxis dataKey="month" angle={-45} textAnchor="end" height={86} />
               <YAxis yAxisId="left" label={{ value: "Number of Trucks", angle: -90, position: "insideLeft" }} />
               <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[75, 100]}
                  tickFormatter={(v) => `${v}%`}
                  label={{ value: "% Rectified", angle: 90, position: "insideRight" }}
               />
               <Tooltip />
               <Legend verticalAlign="top" height={36} />

               <Line
                  yAxisId="left"
                  dataKey="total"
                  name="Total Inspections"
                  fill="#F28E2B"
                  type="monotone"
                  style={{ cursor: "pointer" }}
                  activeDot={{
                     r: 6,
                     onClick: (_: any, payload: any) => openFor("all", payload?.payload),
                  } as any}
               />
               <Bar
                  yAxisId="left"
                  dataKey="found"
                  name="Found with Defect"
                  fill="#F28E2B"
                  radius={[4, 4, 0, 0]}
                  style={{ cursor: "pointer" }}
                  onClick={(d: any) => openFor("with_defects", d?.payload || d)}
               />
               <Bar
                  yAxisId="left"
                  dataKey="rectified"
                  name="Rectified"
                  fill="#59A14F"
                  radius={[4, 4, 0, 0]}
                  style={{ cursor: "pointer" }}
                  onClick={(d: any) => openFor("rectified", d?.payload || d)}
               />
               <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="percent"
                  name="% Rectified"
                  stroke="#E15759"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{
                     r: 6,
                     onClick: (_: any, payload: any) => openFor("rectified", payload?.payload),
                  } as any}
                  style={{ cursor: "pointer" }}
               />
            </ComposedChart>
         </ResponsiveContainer>
         {selected && (
            <RecordsModal
               title={selected.title}
               filter={selected.filter}
               onClose={() => setSelected(null)}
            />
         )}
      </div>
   );
}
